/** Format checks for direct-deposit bank details (screen 11). These are shape checks only: they do not prove the account exists or belongs to the
 * employee, and nothing here stores, logs or sends a value. */

export interface BankDetailsInput {
  bankName: string;
  routingNumber: string;
  accountNumber: string;
}

export type BankDetailsErrors = Partial<Record<keyof BankDetailsInput, string>>;

/** The ABA routing-number checksum: 3(d1+d4+d7) + 7(d2+d5+d8) + (d3+d6+d9) must be a multiple of 10. */
export function isValidRoutingChecksum(routing: string): boolean {
  if (!/^\d{9}$/.test(routing)) return false;
  const d = [...routing].map(Number);
  const sum = 3 * (d[0]! + d[3]! + d[6]!) + 7 * (d[1]! + d[4]! + d[7]!) + (d[2]! + d[5]! + d[8]!);
  return sum % 10 === 0;
}

/** Strips the spaces and dashes people type; everything else is left for the check to reject. */
export const digitsOnly = (value: string) => value.replace(/[\s-]/g, '');

export function validateBankDetails(input: BankDetailsInput): BankDetailsErrors {
  const errors: BankDetailsErrors = {};
  const name = input.bankName.trim();
  if (!name) errors.bankName = 'Enter the bank name.';
  else if (name.length < 2 || name.length > 100) errors.bankName = 'The bank name must be 2 to 100 characters.';

  const routing = digitsOnly(input.routingNumber);
  if (!routing) errors.routingNumber = 'Enter the 9-digit bank routing number.';
  else if (!/^\d{9}$/.test(routing)) errors.routingNumber = 'The routing number must be exactly 9 digits.';
  else if (/^0{9}$/.test(routing) || !isValidRoutingChecksum(routing)) errors.routingNumber = 'This routing number is not valid. Check it and try again.';

  const account = digitsOnly(input.accountNumber);
  if (!account) errors.accountNumber = 'Enter the bank account number.';
  else if (!/^\d{4,17}$/.test(account)) errors.accountNumber = 'The account number must be 4 to 17 digits.';
  return errors;
}
